import { useRef, useState } from 'react';
import {
  createMeerwerk,
  getMeerwerkDefaults,
  MEERWERK_TYPE_LABELS,
  MEERWERK_UNIT_LABELS,
  type MeerwerkType,
  type MeerwerkUnit,
} from '@/api/meerwerken';

/* ─── Admin: inline meerwerk create form (on behalf of one onderaannemer) ─── */

const MEERWERK_TYPES = Object.entries(MEERWERK_TYPE_LABELS) as [MeerwerkType, string][];
const MEERWERK_UNITS = Object.entries(MEERWERK_UNIT_LABELS) as [MeerwerkUnit, string][];

interface AdminMeerwerkFormProps {
  projectId: number;
  subcontractorId: number;
  onCreated: () => Promise<void>;
  onCancel: () => void;
}

export function AdminMeerwerkForm({ projectId, subcontractorId, onCreated, onCancel }: AdminMeerwerkFormProps) {
  const [type, setType] = useState<MeerwerkType>('overig');
  const [unit, setUnit] = useState<MeerwerkUnit>('lump_sum');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isKm = type === 'kilometervergoeding';
  // Binddraad: counted in rollen, price per rol prefilled from the Odoo product.
  const isBinddraad = type === 'binddraad';

  // The type the latest change picked, so a slow defaults fetch cannot prefill the
  // binddraad price into a type the user has already moved on from.
  const typeRef = useRef(type);

  const handleTypeChange = (newType: MeerwerkType) => {
    typeRef.current = newType;
    setType(newType);
    if (newType === 'kilometervergoeding') {
      setUnit('km');
      setUnitPrice('');
    } else if (newType === 'wachturen') {
      setUnit('hours'); // werkuren → per hour
    } else if (newType === 'binddraad') {
      setUnit('pieces');
      getMeerwerkDefaults()
        .then((d) => {
          if (typeRef.current === 'binddraad' && d.binddraad_unit_price !== null) setUnitPrice(String(d.binddraad_unit_price).replace('.', ','));
        })
        .catch(() => {}); // no default → the field stays empty and the server fills it
    } else if (unit === 'km') {
      setUnit('lump_sum');
    }
  };

  // For km: price is server-determined and the description is optional (defaults to
  // "Kilometervergoeding"), so only the kilometer count is required.
  // km needs no description (type + count say it all) but DOES need a positive kilometer
  // count; it bills on a server rate, so no unit_price. Non-km still needs name + price.
  // Binddraad needs only a roll count: no description, and an empty price takes the default.
  const canSave =
    (isKm || isBinddraad
      ? Number(quantity) > 0
      : name.trim().length > 0 && Number(unitPrice) > 0) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await createMeerwerk(projectId, {
        name: name.trim() || (isKm ? 'Kilometervergoeding' : isBinddraad ? 'Binddraad' : ''),
        type,
        unit,
        quantity: Number(quantity) || 1,
        ...(isKm || (isBinddraad && unitPrice.trim() === '')
          ? {}
          : { unit_price: Number(unitPrice.replace(',', '.')) }),
        note: note.trim() || undefined,
        subcontractor_id: subcontractorId,
      });
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 py-3 bg-td-blue/5 border-b border-td-blue/20">
      <p className="text-xs font-bold text-td-navy mb-3 uppercase tracking-wide">Meerwerk toevoegen</p>

      {error && (
        <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Omschrijving */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-td-navy mb-1" htmlFor="mw-name">
            Omschrijving {(isKm || isBinddraad) && <span className="text-td-muted font-normal">(optioneel)</span>}
          </label>
          <input
            id="mw-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={isKm ? 'Kilometervergoeding' : isBinddraad ? 'Binddraad' : 'Bijv. Extra isolatie'}
            className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-300 focus:border-td-blue focus:outline-none"
          />
        </div>

        {/* Type */}
        <div>
          <label className="block text-xs font-semibold text-td-navy mb-1" htmlFor="mw-type">
            Type
          </label>
          <select
            id="mw-type"
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as MeerwerkType)}
            className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-300 focus:border-td-blue focus:outline-none bg-white"
          >
            {MEERWERK_TYPES.map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
        </div>

        {/* Eenheid — hidden for km and binddraad (forced to 'km' / rollen automatically) */}
        {!isKm && !isBinddraad && (
          <div>
            <label className="block text-xs font-semibold text-td-navy mb-1" htmlFor="mw-unit">
              Eenheid
            </label>
            <select
              id="mw-unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value as MeerwerkUnit)}
              className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-300 focus:border-td-blue focus:outline-none bg-white"
            >
              {MEERWERK_UNITS.filter(([v]) => v !== 'km').map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Aantal / Kilometers */}
        <div>
          <label className="block text-xs font-semibold text-td-navy mb-1" htmlFor="mw-qty">
            {isKm ? 'Kilometers' : isBinddraad ? 'Rollen' : 'Aantal'}
          </label>
          <input
            id="mw-qty"
            type="number"
            min="1"
            step="0.01"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-300 focus:border-td-blue focus:outline-none"
          />
        </div>

        {/* Eenheidsprijs — hidden for km (server applies the configured rate) */}
        {!isKm && (
          <div>
            <label className="block text-xs font-semibold text-td-navy mb-1" htmlFor="mw-price">
              {isBinddraad ? 'Prijs per rol (€)' : 'Eenheidsprijs (€)'}
            </label>
            <input
              id="mw-price"
              type="text"
              inputMode="decimal"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              placeholder="0,00"
              className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-300 focus:border-td-blue focus:outline-none"
            />
          </div>
        )}

        {/* Notitie */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-td-navy mb-1" htmlFor="mw-note">
            Notitie <span className="font-normal text-gray-400">(optioneel)</span>
          </label>
          <input
            id="mw-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder=""
            className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-300 focus:border-td-blue focus:outline-none"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-1.5 text-xs font-semibold text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="px-4 py-1.5 text-xs font-bold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
      </div>
    </div>
  );
}
